import { Landmark, Lock, ShieldCheck, ShieldAlert } from "lucide-react";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "");

export const BankVerifiedPill = ({ v, testId = "bank-verified-pill" }) => {
  if (!v?.at) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-wide px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" data-testid={testId}>
      <Landmark size={11} /> Bank details verified in ERPNext · {fmtDate(v.at)}{v.by ? `, by ${v.by}` : ""} <Lock size={10} className="opacity-70" />
    </span>
  );
};

export const KycBadge = ({ k, testId }) => {
  if (!k) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5" data-testid={testId}>
      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${k.ok ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-red-500/15 text-red-300 border-red-500/40"}`}>
        {k.ok ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />} {k.ok ? "Format & checksum valid" : k.error}
      </span>
      {k.ok && k.warning && <span className="text-[10px] text-amber-300">{k.warning}</span>}
      {k.govt && <span className={`text-[10px] ${k.govt.status === "configured" ? "text-sky-300" : "text-slate-500"}`}>{k.govt.label}</span>}
    </span>
  );
};

// Client-side mirrors of backend/kyc.py so candidates get instant feedback while typing.
const D = [[0,1,2,3,4,5,6,7,8,9],[1,2,3,4,0,6,7,8,9,5],[2,3,4,0,1,7,8,9,5,6],[3,4,0,1,2,8,9,5,6,7],[4,0,1,2,3,9,5,6,7,8],[5,9,8,7,6,0,4,3,2,1],[6,5,9,8,7,1,0,4,3,2],[7,6,5,9,8,2,1,0,4,3],[8,7,6,5,9,3,2,1,0,4],[9,8,7,6,5,4,3,2,1,0]];
const P = [[0,1,2,3,4,5,6,7,8,9],[1,5,7,6,2,8,3,0,9,4],[5,8,0,3,7,9,6,1,4,2],[8,9,1,6,0,4,3,5,2,7],[9,4,5,3,1,2,6,8,7,0],[4,2,8,6,5,7,3,9,0,1],[2,7,9,3,8,0,6,4,1,5],[7,0,4,6,9,1,3,2,5,8]];
export const verhoeffOk = (num) => {
  let c = 0;
  const r = num.split("").reverse();
  for (let i = 0; i < r.length; i++) c = D[c][P[i % 8][parseInt(r[i], 10)]];
  return c === 0;
};
export const checkAadhaar = (v) => {
  const d = (v || "").replace(/\D/g, "");
  if (!d) return null;
  if (d.length !== 12) return { ok: false, error: `Aadhaar must be exactly 12 digits (${d.length} entered).` };
  if ("01".includes(d[0])) return { ok: false, error: "Aadhaar numbers never start with 0 or 1." };
  if (!verhoeffOk(d)) return { ok: false, error: "Aadhaar checksum failed — one or more digits are wrong. Please re-check against the card." };
  return { ok: true };
};
export const checkPan = (v, fullName = "") => {
  const p = (v || "").replace(/\s/g, "").toUpperCase();
  if (!p) return null;
  if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(p)) return { ok: false, error: "PAN must be 10 characters like ABCDE1234F (5 letters, 4 digits, 1 letter)." };
  if (p[3] !== "P") return { ok: false, error: "The 4th character of an individual's PAN must be 'P'." };
  const initials = new Set((fullName || "").split(/[^A-Za-z]+/).filter((x) => x && !["mr", "mrs", "ms", "dr"].includes(x.toLowerCase())).map((x) => x[0].toUpperCase()));
  const res = { ok: true };
  if (initials.size && !initials.has(p[4])) res.warning = `5th letter '${p[4]}' should be the initial of your surname as per PAN records — please double-check.`;
  return res;
};
